import { NavigateFunction } from "react-router-dom";
import { IconPlayerPlay, IconTrash, IconEdit } from "@tabler/icons-react";
import { Button, Typography, Badge } from "@altimateai/lego";
import { Stack, Popconfirm } from "@altimateai/lego";
import { TestCase, CreateTestCaseRequest } from "../../services/evals";
import { formatDateTime } from "../utils";

interface TestCaseTableRowProps {
  testCase: TestCase;
  isSelected: boolean;
  isExpanded: boolean;
  isEditing: boolean;
  runningTestId: string | null;
  onToggleSelect: () => void;
  onToggleExpand: () => void;
  onEdit: (data: CreateTestCaseRequest | null) => void;
  onRun: () => void;
  onDelete: () => void;
  navigate: NavigateFunction;
}

export const TestCaseTableRow: React.FC<TestCaseTableRowProps> = ({
  testCase: tc,
  isSelected,
  isExpanded,
  isEditing,
  runningTestId,
  onToggleSelect,
  onToggleExpand,
  onEdit,
  onRun,
  onDelete,
  navigate,
}) => {
  const handleRowClick = (e: React.MouseEvent) => {
    // Don't trigger row click if clicking checkbox or action buttons
    if ((e.target as HTMLElement).closest("input, button")) {
      return;
    }
    const newExpandedId = isExpanded ? null : tc.id;
    onToggleExpand();
    if (newExpandedId) {
      navigate(`?tab=test-cases&test_case_expanded_id=${tc.id}`, {
        replace: true,
      });
    } else {
      navigate(`?tab=test-cases`, { replace: true });
    }
  };

  const handleEditClick = () => {
    if (isEditing) {
      // Toggle off edit mode
      onEdit(null);
    } else {
      // Toggle on edit mode
      if (!isExpanded) {
        onToggleExpand();
      }
      onEdit({
        name: tc.name,
        description: tc.description || "",
        turns: tc.turns.map(t => ({
          user_message: t.user_message,
          expected_ai_response: t.expected_ai_response,
        })),
        criteria: tc.criteria,
        tags: tc.tags || [],
        tenant: tc.tenant,
        team_id: tc.team_id,
        custom_agents: tc.custom_agents || [],
      });
    }
  };

  return (
    <tr
      className={`hover:al-bg-gray-50 al-cursor-pointer ${
        isExpanded ? "al-bg-blue-50" : isSelected ? "al-bg-blue-50" : ""
      }`}
      onClick={handleRowClick}
    >
      <td className="al-px-4 al-py-3 al-text-center" onClick={e => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="al-w-4 al-h-4 al-text-blue-600 al-rounded al-cursor-pointer"
        />
      </td>
      <td className="al-px-4 al-py-3 al-max-w-0">
        <Stack direction="row" align="center" gap="sm" className="al-min-w-0">
          <Typography size="sm" className="al-text-gray-400 al-flex-shrink-0">
            {isExpanded ? "▼" : "▶"}
          </Typography>
          <Typography size="sm" weight="medium" className="al-text-gray-900 al-break-words">
            {tc.name}
          </Typography>
        </Stack>
      </td>
      <td className="al-px-4 al-py-3">
        {tc.tags && tc.tags.length > 0 ? (
          <div className="al-w-full al-space-y-1">
            {tc.tags.map((tag, idx) => (
              <Badge
                key={idx}
                className="al-inline-block al-mr-1 al-mb-1 al-text-[11px] al-px-2 al-py-0 !al-bg-cyan-100 !al-text-cyan-700 al-border al-border-cyan-200 al-whitespace-nowrap"
              >
                {tag}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="al-text-gray-400">-</span>
        )}
      </td>
      <td className="al-px-4 al-py-3">
        <Typography size="sm" className="al-text-gray-600">
          {tc.turns.length}
        </Typography>
      </td>
      <td className="al-px-4 al-py-3">
        <Typography size="sm" className="al-text-gray-600">
          {formatDateTime(tc.updated_at)}
        </Typography>
      </td>
      <td className="al-px-4 al-py-3">
        <Typography size="sm" className="al-text-gray-600">
          {tc.updated_by || "-"}
        </Typography>
      </td>
      <td className="al-px-4 al-py-3" onClick={e => e.stopPropagation()}>
        <Stack direction="row" gap="sm">
          <Button
            onClick={onRun}
            variant="outline"
            size="sm"
            disabled={runningTestId === tc.id}
            className={
              runningTestId === tc.id
                ? "al-bg-green-100 al-text-green-700 al-border-green-300 al-shadow-inner al-translate-y-[1px]"
                : "al-text-green-600 hover:al-text-green-700 hover:al-bg-green-50 al-border-green-200"
            }
          >
            <IconPlayerPlay className="al-w-4 al-h-4" />
          </Button>
          <Button
            onClick={handleEditClick}
            variant="outline"
            size="sm"
            className={
              isEditing
                ? "al-bg-blue-100 al-text-blue-700 al-border-blue-300 al-shadow-inner al-translate-y-[1px]"
                : "al-text-blue-600 hover:al-text-blue-700 hover:al-bg-blue-50 al-border-blue-200"
            }
          >
            <IconEdit className="al-w-4 al-h-4" />
          </Button>
          <Popconfirm
            title="Delete test case"
            description={`Are you sure you want to delete "${tc.name}"?`}
            onConfirm={onDelete}
            okText="Yes"
            cancelText="No"
          >
            <Button
              variant="outline"
              size="sm"
              className="al-text-red-600 hover:al-text-red-700 hover:al-bg-red-50 al-border-red-200"
            >
              <IconTrash className="al-w-4 al-h-4" />
            </Button>
          </Popconfirm>
        </Stack>
      </td>
    </tr>
  );
};
